import { useState, useEffect } from 'react';
import { collection, onSnapshot, addDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useMarket, inPortals } from '../../contexts/MarketContext';
import useBasePath from '../../utils/useBasePath';
import { useT } from '../../contexts/LocaleContext';
import { STATUS_LABELS } from '../../utils/statuses';
import { totalTravellers } from '../../utils/groupSize';

// ── Constants ────────────────────────────────────────────────────────────────

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];
const DAY_NAMES = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
const MAX_LANES = 3; // max event rows per week before "+N more"

const CALENDAR_STATUSES = new Set([
  'quote_approved','deposit_paid','pre_tour','tour_live','completed',
]);

// ── Category config ───────────────────────────────────────────────────────────

function getCategory(status) {
  if (status === 'quote_approved' || status === 'deposit_paid') return 'provisional';
  if (status === 'pre_tour' || status === 'tour_live')           return 'confirmed';
  if (status === 'completed')                                    return 'completed';
  return 'provisional';
}

const CAT = {
  provisional: {
    bar:    'bg-[#F59E0B] hover:bg-[#D97706]',
    text:   'text-[#451A03]',
    dot:    'bg-amber-400',
    badge:  'bg-amber-100 text-amber-800',
    panel:  'bg-amber-50 border-amber-200',
    stripe: 'bg-amber-400',
    label:  'Provisional',
    labelKey: 'calendar.statusProvisional',
  },
  confirmed: {
    bar:    'bg-[#10B981] hover:bg-[#059669]',
    text:   'text-white',
    dot:    'bg-emerald-500',
    badge:  'bg-emerald-100 text-emerald-800',
    panel:  'bg-emerald-50 border-emerald-200',
    stripe: 'bg-emerald-500',
    label:  'Confirmed',
    labelKey: 'calendar.statusConfirmed',
  },
  completed: {
    bar:    'bg-[#94A3B8] hover:bg-[#64748B]',
    text:   'text-white',
    dot:    'bg-slate-400',
    badge:  'bg-slate-100 text-slate-600',
    panel:  'bg-slate-50 border-slate-200',
    stripe: 'bg-slate-400',
    label:  'Completed',
    labelKey: 'calendar.statusCompleted',
  },
};

// ── Staff event colour palette ────────────────────────────────────────────────

const EVENT_COLORS = {
  purple: { bar: 'bg-[#7C3AED] hover:bg-[#6D28D9]', text: 'text-white', dot: 'bg-violet-500',  badge: 'bg-violet-100 text-violet-800',  panel: 'bg-violet-50 border-violet-200',  stripe: 'bg-violet-500',  preview: 'bg-[#7C3AED]' },
  blue:   { bar: 'bg-[#2563EB] hover:bg-[#1D4ED8]', text: 'text-white', dot: 'bg-blue-500',    badge: 'bg-blue-100 text-blue-800',      panel: 'bg-blue-50 border-blue-200',      stripe: 'bg-blue-500',    preview: 'bg-[#2563EB]' },
  pink:   { bar: 'bg-[#DB2777] hover:bg-[#BE185D]', text: 'text-white', dot: 'bg-pink-500',    badge: 'bg-pink-100 text-pink-800',      panel: 'bg-pink-50 border-pink-200',      stripe: 'bg-pink-500',    preview: 'bg-[#DB2777]' },
  teal:   { bar: 'bg-[#0F766E] hover:bg-[#0D6B64]', text: 'text-white', dot: 'bg-teal-600',    badge: 'bg-teal-100 text-teal-800',      panel: 'bg-teal-50 border-teal-200',      stripe: 'bg-teal-600',    preview: 'bg-[#0F766E]' },
  orange: { bar: 'bg-[#EA580C] hover:bg-[#C2410C]', text: 'text-white', dot: 'bg-orange-500',  badge: 'bg-orange-100 text-orange-800',  panel: 'bg-orange-50 border-orange-200',  stripe: 'bg-orange-500',  preview: 'bg-[#EA580C]' },
  red:    { bar: 'bg-[#DC2626] hover:bg-[#B91C1C]', text: 'text-white', dot: 'bg-red-500',     badge: 'bg-red-100 text-red-800',        panel: 'bg-red-50 border-red-200',        stripe: 'bg-red-500',     preview: 'bg-[#DC2626]' },
};

const EVENT_COLOR_KEYS = ['purple', 'blue', 'pink', 'teal', 'orange', 'red'];

// ── Date helpers ──────────────────────────────────────────────────────────────

// Convert JS getDay() (0=Sun) → Monday-first index (0=Mon … 6=Sun)
const toMF = (d) => (d + 6) % 7;

function parseDate(s) {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function fmtDate(d) {
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function fmtShort(d) {
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function nights(dep, ret) {
  return Math.round((ret - dep) / 864e5);
}

// ── Calendar grid builder ─────────────────────────────────────────────────────

function buildWeeks(year, month) {
  const first = new Date(year, month, 1);
  const last  = new Date(year, month + 1, 0);

  // Monday on or before the 1st
  const startOffset = toMF(first.getDay());
  let weekStart = new Date(year, month, 1 - startOffset);

  const weeks = [];
  while (weekStart <= last || weeks.length < 5) {
    const week = [];
    for (let i = 0; i < 7; i++) {
      week.push(new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + i));
    }
    weeks.push(week);
    weekStart.setDate(weekStart.getDate() + 7);
    if (weekStart > last && weeks.length >= 5) break;
    if (weeks.length >= 6) break;
  }
  return weeks;
}

// ── Event bar builder ─────────────────────────────────────────────────────────

function getBarsForWeek(week, allItems) {
  const ws = week[0]; // Monday
  const we = week[6]; // Sunday

  const bars = [];
  for (const t of allItems) {
    const dep = parseDate(t.startDate);
    const ret = parseDate(t.endDate);
    if (dep > we || ret < ws) continue;

    const startCol = dep < ws ? 0 : toMF(dep.getDay());
    const endCol   = ret > we ? 6 : toMF(ret.getDay());

    bars.push({
      ...t,
      startCol,
      endCol,
      isStart: dep >= ws,
      isEnd:   ret <= we,
      dep,
      ret,
    });
  }

  // Sort: earlier starts first; ties broken by longer span (wider bars fill lanes first)
  bars.sort((a, b) =>
    a.startCol !== b.startCol
      ? a.startCol - b.startCol
      : (b.endCol - b.startCol) - (a.endCol - a.startCol)
  );
  return bars;
}

// Greedy lane assignment — puts non-overlapping events in the same row
function assignLanes(bars) {
  const lanes = [];
  for (const bar of bars) {
    let placed = false;
    for (const lane of lanes) {
      const clashes = lane.some(b => b.startCol <= bar.endCol && b.endCol >= bar.startCol);
      if (!clashes) { lane.push(bar); placed = true; break; }
    }
    if (!placed) lanes.push([bar]);
  }
  return lanes;
}

// ── Subcomponents ─────────────────────────────────────────────────────────────

function EventBar({ bar, isSelected, onClick }) {
  const isEvent = bar._source === 'event';
  const style   = isEvent
    ? (EVENT_COLORS[bar.color] || EVENT_COLORS.purple)
    : CAT[getCategory(bar.status)];
  const span    = bar.endCol - bar.startCol + 1;
  const label   = isEvent
    ? (bar.title || '')
    : bar.internalRef || bar.tourName || `${bar.sport || ''}${bar.destination ? ` · ${bar.destination}` : ''}`;

  const rounding =
    bar.isStart && bar.isEnd ? 'rounded-full mx-0.5' :
    bar.isStart              ? 'rounded-l-full ml-0.5 rounded-r-none' :
    bar.isEnd                ? 'rounded-r-full mr-0.5 rounded-l-none' :
                               'rounded-none';

  return (
    <div
      style={{ gridColumnStart: bar.startCol + 1, gridColumnEnd: bar.endCol + 2 }}
      onClick={onClick}
      title={label}
      className={`
        h-[21px] flex items-center px-1.5 cursor-pointer transition-all select-none
        ${style.bar} ${style.text} ${rounding}
        ${isSelected ? 'ring-2 ring-inset ring-white/50' : ''}
      `}
    >
      {bar.isStart && (
        <span className="text-[10px] font-semibold truncate leading-none">
          {span >= 4 ? label : span >= 2 ? label.split(' · ')[0] : ''}
        </span>
      )}
    </div>
  );
}

function DetailPanel({ tour, onClose, navigate, base }) {
  const t = useT();
  const cat   = getCategory(tour.status);
  const style = CAT[cat];
  const n     = nights(tour.dep, tour.ret);

  return (
    <div className="w-full md:w-[290px] md:flex-shrink-0 bg-white rounded-[14px] border border-[#E5E7EB] overflow-hidden md:sticky md:top-6 shadow-[0_4px_18px_rgba(16,24,40,.1)]">
      <div className={`h-[3px] w-full ${style.stripe}`} />
      <div className="p-[18px]">
        <div className="flex items-start justify-between mb-4">
          <div className="min-w-0 flex-1 pr-2">
            <span className={`inline-flex items-center gap-1.5 px-[9px] py-[4px] rounded-[20px] text-[11px] font-bold mb-[10px] ${style.badge}`}>
              <span className={`w-[5px] h-[5px] rounded-full ${style.dot}`} />
              {t(style.labelKey)}
            </span>
            <h3 className="text-[15px] font-extrabold text-[#111827] leading-snug">
              {tour.internalRef || tour.tourName || tour.sport || 'Tour'}
            </h3>
            {tour.destination && (
              <p className="text-[13px] text-[#6B7280] mt-0.5">
                {tour.sport ? `${tour.sport} · ${tour.destination}` : tour.destination}
              </p>
            )}
          </div>
          <button onClick={onClose}
            className="flex-shrink-0 w-7 h-7 rounded-[7px] flex items-center justify-center text-[#9CA3AF] hover:bg-surface hover:text-[#374151] transition">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-[10px] mb-5">
          <div className="flex items-start gap-2.5">
            <svg className="w-4 h-4 text-[#9CA3AF] mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <div>
              <p className="text-[12.5px] font-bold text-[#111827]">{fmtShort(tour.dep)} → {fmtShort(tour.ret)}</p>
              <p className="text-[11px] text-[#9CA3AF]">{n} night{n !== 1 ? 's' : ''}</p>
            </div>
          </div>
          {tour.groupType && (
            <div className="flex items-center gap-2.5">
              <svg className="w-4 h-4 text-[#9CA3AF] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <p className="text-[13px] text-[#374151]">{tour.groupType}</p>
            </div>
          )}
          {totalTravellers(tour) > 0 && (
            <div className="flex items-center gap-2.5">
              <svg className="w-4 h-4 text-[#9CA3AF] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <p className="text-[13px] text-[#374151]">{totalTravellers(tour)} travellers</p>
            </div>
          )}
        </div>

        <div className="mb-5 pt-[12px] border-t border-[#F1F3F6]">
          <p className="text-[10px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-[7px]">Status</p>
          <span className={`inline-flex items-center gap-1.5 px-[9px] py-[4px] rounded-[20px] text-[11.5px] font-bold ${style.badge}`}>
            <span className={`w-[5px] h-[5px] rounded-full ${style.dot}`} />
            {STATUS_LABELS[tour.status] || tour.status}
          </span>
        </div>

        <button
          onClick={() => navigate(`${base}/requests/${tour.id}`)}
          className="w-full bg-pb hover:bg-navy text-white text-[13px] font-bold py-[10px] rounded-[9px] transition flex items-center justify-center gap-1.5"
        >
          View Tour
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// ── Add Event Modal ───────────────────────────────────────────────────────────

function AddEventModal({ onSave, onClose, saving, defaultDate }) {
  const t = useT();
  const fallback = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({
    title:     '',
    startDate: defaultDate || fallback,
    endDate:   defaultDate || fallback,
    color:     'purple',
    notes:     '',
  });

  function set(k, v) { setForm((p) => ({ ...p, [k]: v })); }

  function handleSubmit(e) {
    e.preventDefault();
    if (!form.title.trim() || !form.startDate || !form.endDate) return;
    onSave(form);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full sm:max-w-[440px] bg-white rounded-t-[16px] sm:rounded-[16px] shadow-xl overflow-hidden">

        {/* Header */}
        <div className="px-5 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
          <h3 className="text-[16px] font-bold text-[#111827]">{t('calendar.addTeamEvent')}</h3>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-surface hover:text-[#374151] transition"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-5 py-5 space-y-4">

          {/* Title */}
          <div>
            <label className="block text-[12px] font-semibold text-[#374151] mb-1.5">
              Title <span className="text-dr">*</span>
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              required
              autoFocus
              placeholder="e.g. Team Meeting, Training Day…"
              className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2.5 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
            />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[12px] font-semibold text-[#374151] mb-1.5">
                Start date <span className="text-dr">*</span>
              </label>
              <input
                type="date"
                value={form.startDate}
                onChange={(e) => {
                  set('startDate', e.target.value);
                  if (e.target.value > form.endDate) set('endDate', e.target.value);
                }}
                required
                className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2.5 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
              />
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-[#374151] mb-1.5">
                End date <span className="text-dr">*</span>
              </label>
              <input
                type="date"
                value={form.endDate}
                min={form.startDate}
                onChange={(e) => set('endDate', e.target.value)}
                required
                className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2.5 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
              />
            </div>
          </div>

          {/* Colour */}
          <div>
            <label className="block text-[12px] font-semibold text-[#374151] mb-2">Colour</label>
            <div className="flex items-center gap-2.5">
              {EVENT_COLOR_KEYS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => set('color', c)}
                  className={`w-7 h-7 rounded-full ${EVENT_COLORS[c].preview} transition-transform ${
                    form.color === c ? 'ring-2 ring-offset-2 ring-[#374151] scale-110' : 'hover:scale-110'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-[12px] font-semibold text-[#374151] mb-1.5">Notes (optional)</label>
            <textarea
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              rows={2}
              placeholder="Any additional details…"
              className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2.5 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb resize-none"
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-2 pt-1">
            <button
              type="submit"
              disabled={saving || !form.title.trim()}
              className="flex-1 bg-pb hover:bg-navy text-white text-[13px] font-bold py-[10px] rounded-[9px] transition disabled:opacity-60"
            >
              {saving ? 'Saving…' : 'Save event'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-5 text-[13px] font-semibold text-[#6B7280] hover:text-[#374151] transition"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Staff Event Detail Panel ───────────────────────────────────────────────────

function StaffEventDetailPanel({ event, onClose, onDelete, deleting }) {
  const t = useT();
  const style = EVENT_COLORS[event.color] || EVENT_COLORS.purple;
  const dep = parseDate(event.startDate);
  const ret = parseDate(event.endDate);
  const n   = nights(dep, ret);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full sm:max-w-[380px] bg-white rounded-t-[16px] sm:rounded-[16px] shadow-xl overflow-hidden">

        {/* Colour stripe */}
        <div className={`h-1.5 w-full ${style.stripe}`} />

        <div className="p-5">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="min-w-0 flex-1 pr-2">
              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide mb-2 ${style.badge}`}>
                <span className={`w-[5px] h-[5px] rounded-full ${style.dot}`} />
                {t('calendar.teamEvent')}
              </span>
              <h3 className="text-[15px] font-bold text-[#111827] leading-snug">{event.title}</h3>
              <p className="text-[12px] text-[#6B7280] mt-0.5">{t('calendar.addedBy', { name: event.createdBy })}</p>
            </div>
            <button
              onClick={onClose}
              className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-surface hover:text-[#374151] transition"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Date */}
          <div className="flex items-start gap-2.5 mb-4">
            <svg className="w-4 h-4 text-[#9CA3AF] mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <div>
              <p className="text-[12px] font-semibold text-[#111827]">
                {n === 0 ? fmtDate(dep) : `${fmtShort(dep)} → ${fmtShort(ret)}`}
              </p>
              {n > 0 && <p className="text-[11px] text-[#9CA3AF]">{n} day{n !== 1 ? 's' : ''}</p>}
            </div>
          </div>

          {/* Notes */}
          {event.notes && (
            <div className="mb-4 p-3 bg-surface rounded-[8px]">
              <p className="text-[13px] text-[#374151] whitespace-pre-line">{event.notes}</p>
            </div>
          )}

          {/* Delete */}
          <button
            onClick={() => onDelete(event.id)}
            disabled={deleting}
            className="w-full text-center text-[13px] font-bold text-dr/70 hover:text-dr py-[10px] border border-dr/20 hover:border-dr/40 rounded-[9px] transition disabled:opacity-50"
          >
            {deleting ? 'Deleting…' : 'Delete event'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function TourCalendar() {
  const navigate       = useNavigate();
  const { currentUser } = useAuth();
  const { portals }    = useMarket();
  const base           = useBasePath();
  const t              = useT();
  const today          = new Date();

  const [viewYear,      setViewYear]      = useState(today.getFullYear());
  const [viewMonth,     setViewMonth]     = useState(today.getMonth());
  const [tours,         setTours]         = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [selected,      setSelected]      = useState(null);       // selected tour bar
  const [selectedEvent, setSelectedEvent] = useState(null);       // selected staff event bar
  const [showAddModal,  setShowAddModal]  = useState(false);
  const [savingEvent,   setSavingEvent]   = useState(false);
  const [deletingId,    setDeletingId]    = useState(null);

  // Tours
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'requests'), (snap) => {
      setTours(
        snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((d) => d.departureDate && d.returnDate && CALENDAR_STATUSES.has(d.status) && inPortals(d, portals)
            && (!d.crmOrigin || d.crmFullyConverted))
      );
    });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portals.join(',')]);

  // Staff calendar events
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'calendarEvents'), (snap) => {
      setCalendarEvents(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, []);

  async function handleAddEvent(form) {
    setSavingEvent(true);
    try {
      await addDoc(collection(db, 'calendarEvents'), {
        ...form,
        title:      form.title.trim(),
        // Scope the event to the current portal when viewing a single one. In the
        // combined "All" view (or any multi-portal view) leave it null = shows on
        // every portal, matching legacy company-wide events.
        portal:     portals.length === 1 ? portals[0] : null,
        createdBy:  currentUser?.displayName || currentUser?.email || 'Staff',
        createdByUid: currentUser?.uid || '',
        createdAt:  serverTimestamp(),
      });
      setShowAddModal(false);
    } finally {
      setSavingEvent(false);
    }
  }

  async function handleDeleteEvent(id) {
    if (!window.confirm('Delete this event? This cannot be undone.')) return;
    setDeletingId(id);
    try {
      await deleteDoc(doc(db, 'calendarEvents', id));
      setSelectedEvent(null);
    } finally {
      setDeletingId(null);
    }
  }

  function prevMonth() {
    if (viewMonth === 0) { setViewMonth(11); setViewYear((y) => y - 1); }
    else setViewMonth((m) => m - 1);
  }

  function nextMonth() {
    if (viewMonth === 11) { setViewMonth(0); setViewYear((y) => y + 1); }
    else setViewMonth((m) => m + 1);
  }

  const weeks = buildWeeks(viewYear, viewMonth);
  const isCurrentMonth = viewYear === today.getFullYear() && viewMonth === today.getMonth();

  // Team events visible in the current view: those tagged for one of the active
  // portals, plus untagged (legacy / company-wide) events. In the "All" view
  // `portals` covers every key, so every event shows.
  const visibleEvents = calendarEvents.filter(
    (e) => !e.portal || portals.includes(e.portal)
  );

  // Normalise tours + staff events into one array with startDate / endDate
  const allItems = [
    ...tours.map((t) => ({ ...t, startDate: t.departureDate, endDate: t.returnDate, _source: 'tour' })),
    ...visibleEvents.filter((e) => e.startDate && e.endDate).map((e) => ({ ...e, _source: 'event' })),
  ];

  // Summary counts for this month
  const mStart = new Date(viewYear, viewMonth, 1);
  const mEnd   = new Date(viewYear, viewMonth + 1, 0);
  const monthTours = tours.filter((t) => {
    const dep = parseDate(t.departureDate);
    const ret = parseDate(t.returnDate);
    return dep <= mEnd && ret >= mStart;
  });
  const monthEvents = visibleEvents.filter((e) => {
    if (!e.startDate || !e.endDate) return false;
    return parseDate(e.startDate) <= mEnd && parseDate(e.endDate) >= mStart;
  });
  const provisionalCount = monthTours.filter((t) => getCategory(t.status) === 'provisional').length;
  const confirmedCount   = monthTours.filter((t) => getCategory(t.status) === 'confirmed').length;

  return (
    <div className="space-y-5">
      {/* ── Page header ── */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-[23px] font-extrabold text-navy tracking-[-0.4px]">{t('calendar.title')}</h1>
          <p className="text-[13px] text-[#6B7280] mt-1">{t('calendar.subtitle')}</p>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-5 items-start">

      {/* ── Calendar card ── */}
      <div className="flex-1 min-w-0 bg-white rounded-[14px] border border-[#E5E7EB] overflow-hidden shadow-[0_1px_2px_rgba(16,24,40,.04)]">

        {/* Header */}
        <div className="px-5 py-[14px] border-b border-[#F1F3F6]">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-5">
              <h2 className="text-[17px] font-extrabold text-navy min-w-[160px]">
                {MONTH_NAMES[viewMonth]} {viewYear}
              </h2>
              <div className="hidden sm:flex items-center gap-4">
                {Object.entries(CAT).map(([key, val]) => (
                  <div key={key} className="flex items-center gap-1.5">
                    <span className={`w-[9px] h-[9px] rounded-full flex-shrink-0 ${val.dot}`} />
                    <span className="text-[12px] text-[#6B7280] font-semibold">{t(val.labelKey)}</span>
                  </div>
                ))}
                {visibleEvents.length > 0 && (
                  <div className="flex items-center gap-1.5">
                    <span className="w-[9px] h-[9px] rounded-full flex-shrink-0 bg-violet-500" />
                    <span className="text-[12px] text-[#6B7280] font-semibold">{t('calendar.teamEvent')}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {provisionalCount > 0 && (
                <span className="hidden md:inline-flex items-center gap-1.5 px-[9px] py-[4px] rounded-[20px] text-[11.5px] font-bold bg-amber-100 text-amber-800">
                  <span className="w-[5px] h-[5px] rounded-full bg-amber-400" />{provisionalCount} provisional
                </span>
              )}
              {confirmedCount > 0 && (
                <span className="hidden md:inline-flex items-center gap-1.5 px-[9px] py-[4px] rounded-[20px] text-[11.5px] font-bold bg-emerald-100 text-emerald-800">
                  <span className="w-[5px] h-[5px] rounded-full bg-emerald-500" />{confirmedCount} confirmed
                </span>
              )}
              {!isCurrentMonth && (
                <button onClick={() => { setViewYear(today.getFullYear()); setViewMonth(today.getMonth()); }}
                  className="text-[12.5px] font-bold text-pb hover:text-navy border border-[#E5E7EB] hover:border-pb/40 px-3 py-[7px] rounded-[9px] transition">
                  Today
                </button>
              )}
              <button onClick={() => setShowAddModal(true)}
                className="flex items-center gap-1.5 bg-pb hover:bg-navy text-white text-[12.5px] font-bold px-[12px] py-[7px] rounded-[9px] transition">
                <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
                <span className="hidden sm:inline">{t('calendar.addEvent')}</span>
              </button>
              <div className="flex items-center bg-white border border-[#E5E7EB] rounded-[9px] overflow-hidden">
                <button onClick={prevMonth} className="px-3 py-[7px] text-[#6B7280] hover:bg-surface hover:text-[#374151] transition">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                </button>
                <div className="w-px h-5 bg-[#E5E7EB]" />
                <button onClick={nextMonth} className="px-3 py-[7px] text-[#6B7280] hover:bg-surface hover:text-[#374151] transition">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Day-of-week headers */}
        <div className="grid grid-cols-7 bg-surface border-b border-[#E5E7EB]">
          {DAY_NAMES.map((d, i) => (
            <div key={d} className={`py-[10px] text-center text-[10.5px] font-extrabold uppercase tracking-[0.7px] ${i >= 5 ? 'text-[#C7CDD6]' : 'text-[#9CA3AF]'}`}>
              {d}
            </div>
          ))}
        </div>

        {/* Weeks */}
        {weeks.map((week, wi) => {
          const bars   = getBarsForWeek(week, allItems);
          const lanes  = assignLanes(bars);
          const visible = lanes.slice(0, MAX_LANES);

          // Count hidden events per column for the overflow row
          const hiddenBars = lanes.slice(MAX_LANES).flatMap((l) => l);
          const overflowByCol = Array.from({ length: 7 }, (_, col) =>
            hiddenBars.filter((b) => b.startCol <= col && b.endCol >= col).length
          );
          const hasOverflow = hiddenBars.length > 0;

          return (
            <div
              key={wi}
              className="border-b border-[#E5E7EB] last:border-b-0"
              style={{ minHeight: '100px' }}
            >
              {/* Date numbers */}
              <div className="grid grid-cols-7">
                {week.map((day, di) => {
                  const isToday   = day.toDateString() === today.toDateString();
                  const inMonth   = day.getMonth() === viewMonth;
                  const isWeekend = di >= 5;

                  return (
                    <div
                      key={di}
                      className={`pt-2 pb-1 px-2 text-right ${isWeekend ? 'bg-[#FAFAFA]' : ''}`}
                    >
                      <span className={`
                        inline-flex items-center justify-center w-[26px] h-[26px] rounded-full text-[13px]
                        ${isToday
                          ? 'bg-pb text-white font-bold'
                          : inMonth
                          ? isWeekend ? 'text-[#9CA3AF] font-medium' : 'text-[#374151] font-medium'
                          : 'text-[#D1D5DB]'
                        }
                      `}>
                        {day.getDate()}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Event lanes */}
              <div className="pb-1.5 px-0 space-y-px">
                {visible.map((lane, li) => (
                  <div key={li} className="grid grid-cols-7" style={{ minHeight: '22px' }}>
                    {lane.map((bar) => (
                      <EventBar
                        key={bar.id}
                        bar={bar}
                        isSelected={
                          bar._source === 'event'
                            ? selectedEvent?.id === bar.id
                            : selected?.id === bar.id
                        }
                        onClick={() => {
                          if (bar._source === 'event') {
                            setSelectedEvent((s) => s?.id === bar.id ? null : bar);
                            setSelected(null);
                          } else {
                            setSelected((s) => s?.id === bar.id ? null : bar);
                            setSelectedEvent(null);
                          }
                        }}
                      />
                    ))}
                  </div>
                ))}

                {/* Overflow "+N" indicators per column */}
                {hasOverflow && (
                  <div className="grid grid-cols-7 h-[18px]">
                    {overflowByCol.map((count, col) => (
                      <div key={col} className="flex items-center justify-center">
                        {count > 0 && (
                          <span className="text-[10px] font-semibold text-[#9CA3AF]">
                            +{count}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}

      </div>

      {/* ── Tour detail panel ── */}
      {selected && (
        <DetailPanel
          tour={selected}
          onClose={() => setSelected(null)}
          navigate={navigate}
          base={base}
        />
      )}

      {/* ── Staff event detail modal ── */}
      {selectedEvent && (
        <StaffEventDetailPanel
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          onDelete={handleDeleteEvent}
          deleting={deletingId === selectedEvent.id}
        />
      )}

      {/* ── Add Event modal ── */}
      {showAddModal && (
        <AddEventModal
          onSave={handleAddEvent}
          onClose={() => setShowAddModal(false)}
          saving={savingEvent}
        />
      )}

      {/* Mobile event count */}
      {monthEvents.length > 0 && (
        <div className="md:hidden flex items-center gap-2 px-1">
          <span className="w-[9px] h-[9px] rounded-full bg-violet-500 flex-shrink-0" />
          <span className="text-[12px] text-[#6B7280] font-semibold">
            {monthEvents.length} team event{monthEvents.length !== 1 ? 's' : ''} this month
          </span>
        </div>
      )}
      </div>
    </div>
  );
}
